/**
 * Floor-plan editor geometry (10-web-admin § 6.3, § 6.4).
 *
 * Pure. Everything a drag, a nudge or a duplicate does to an object is decided
 * here, so the canvas component stays a renderer and the fiddly parts — snap,
 * bounds, the next free number — are testable without a pointer.
 *
 * All coordinates are centimetres. Pixels exist only in the view transform.
 */

import type { paths } from '@cowoo/api-client';

export interface EditorObject {
  resourceId: string;
  kind: 'desk' | 'room' | 'locker' | 'parking' | 'office';
  label: string;
  zone: string | null;
  xCm: number;
  yCm: number;
  wCm: number;
  hCm: number;
  rotationDeg: number;
  deskType: 'flex' | 'private' | null;
  status: 'bookable' | 'blocked';
  /** § 6.4 — the property panel's fields, saved with the placement. */
  attributes: string[];
  capacity: number | null;
  bookableBy: BookableBy[];
  maxAdvanceDays: MaxAdvanceDays;
}

/**
 * A product key (server 03 § resource_placement): a desk's `flex_desk` /
 * `day_ticket`, a room's `meeting_block`; what a contract assigns has none.
 * The panel offers a desk the first two; the rest is round-tripped as is.
 */
export type BookableBy = NonNullable<
  NonNullable<
    paths['/admin/floor-plan-versions/{vid}']['put']['requestBody']
  >['content']['application/json']['placements'][number]['bookableBy']
>[number];
export type MaxAdvanceDays = 7 | 14 | 21 | 30;
export const MAX_ADVANCE_DAYS: readonly MaxAdvanceDays[] = [7, 14, 21, 30];
/** § 6.4 — "Ausstattung": at most ten tags of at most forty characters. */
export const MAX_ATTRIBUTES = 10;
export const MAX_ATTRIBUTE_LENGTH = 40;
export const CAPACITY_RANGE = { min: 1, max: 30 } as const;

/** What a freshly placed object carries in the fields the panel edits. */
export const DEFAULT_RULES: Pick<EditorObject, 'attributes' | 'capacity' | 'bookableBy' | 'maxAdvanceDays'> =
  {
    attributes: [],
    capacity: null,
    bookableBy: ['flex_desk', 'day_ticket'],
    maxAdvanceDays: 14,
  };

export interface Bounds {
  widthCm: number;
  heightCm: number;
}

/** § 6.3 — the grid sizes the toolbar offers. */
export const GRID_SIZES_CM = [10, 25, 50, 100] as const;
/** Smart guides engage within this many **screen** pixels. */
export const GUIDE_SNAP_PX = 8;
/** § 6.3 — rotation snaps to this, or to 90° with the plain `R` shortcut. */
export const ROTATION_STEP_DEG = 15;
/** § 6.3 — undo history depth, in memory only. */
export const HISTORY_LIMIT = 100;

/** Defaults per kind (§ 6.3 tool rail). */
export const DEFAULT_SIZE_CM: Record<EditorObject['kind'], { wCm: number; hCm: number }> = {
  desk: { wCm: 120, hCm: 60 },
  room: { wCm: 400, hCm: 300 },
  locker: { wCm: 40, hCm: 50 },
  parking: { wCm: 250, hCm: 500 },
  office: { wCm: 500, hCm: 400 },
};

export function snapToGrid(valueCm: number, gridCm: number, enabled: boolean): number {
  if (!enabled || gridCm <= 0) return Math.round(valueCm);
  return Math.round(valueCm / gridCm) * gridCm;
}

/**
 * § 6.3 — an object may not leave the plan. Clamping the position rather than
 * refusing the drag keeps a drag that overshoots from feeling broken.
 */
export function clampToBounds(object: EditorObject, bounds: Bounds): EditorObject {
  return {
    ...object,
    xCm: Math.min(Math.max(0, object.xCm), Math.max(0, bounds.widthCm - object.wCm)),
    yCm: Math.min(Math.max(0, object.yCm), Math.max(0, bounds.heightCm - object.hCm)),
  };
}

export interface MoveInput {
  object: EditorObject;
  deltaXCm: number;
  deltaYCm: number;
  gridCm: number;
  snap: boolean;
  bounds: Bounds;
}

/** Snap first, then clamp: an object nudged into a wall must still sit on the grid. */
export function moveObject(input: MoveInput): EditorObject {
  const moved = {
    ...input.object,
    xCm: snapToGrid(input.object.xCm + input.deltaXCm, input.gridCm, input.snap),
    yCm: snapToGrid(input.object.yCm + input.deltaYCm, input.gridCm, input.snap),
  };
  return clampToBounds(moved, input.bounds);
}

/** § 6.3 — `R` turns by 90°, `Shift`+`R` by 15°; both stay inside 0–359. */
export function rotateObject(object: EditorObject, degrees: number): EditorObject {
  return { ...object, rotationDeg: (((object.rotationDeg + degrees) % 360) + 360) % 360 };
}

/**
 * § 6.3 — the next free number. Desks are numbered plainly, so the next one is
 * the lowest positive integer nobody is using; a label like `O3` keeps its
 * prefix and takes the next free number within it.
 */
export function nextFreeLabel(objects: readonly EditorObject[], prefix = ''): string {
  const pattern = new RegExp(`^${prefix}(\\d+)$`);
  const taken = new Set<number>();
  for (const object of objects) {
    const match = pattern.exec(object.label);
    if (match?.[1]) taken.add(Number(match[1]));
  }
  let candidate = 1;
  while (taken.has(candidate)) candidate += 1;
  return `${prefix}${candidate}`;
}

/** § 6.3 `Cmd+D` — one grid step away, so the copy is visibly a second object. */
export function duplicateObject(
  object: EditorObject,
  all: readonly EditorObject[],
  gridCm: number,
  bounds: Bounds,
  newId: string,
): EditorObject {
  const copy: EditorObject = {
    ...object,
    resourceId: newId,
    label: nextFreeLabel(all, labelPrefix(object.label)),
    xCm: object.xCm + gridCm,
    yCm: object.yCm + gridCm,
  };
  return clampToBounds(copy, bounds);
}

function labelPrefix(label: string): string {
  return /^([A-Za-z]*)\d+$/.exec(label)?.[1] ?? '';
}

/* -------------------------------- guides ---------------------------------- */

export interface Guide {
  axis: 'x' | 'y';
  /** Where the line is drawn, in cm. */
  atCm: number;
}

/**
 * § 6.3 — smart guides on the edges and centres of nearby objects. The
 * threshold is given in screen pixels, so zooming out does not turn the whole
 * plan into one magnet.
 */
export function smartGuides(
  moving: EditorObject,
  others: readonly EditorObject[],
  pxPerCm: number,
  thresholdPx = GUIDE_SNAP_PX,
): Guide[] {
  const thresholdCm = thresholdPx / Math.max(pxPerCm, 0.0001);
  const guides: Guide[] = [];

  const xs = (object: EditorObject): number[] => [
    object.xCm,
    object.xCm + object.wCm / 2,
    object.xCm + object.wCm,
  ];
  const ys = (object: EditorObject): number[] => [
    object.yCm,
    object.yCm + object.hCm / 2,
    object.yCm + object.hCm,
  ];

  for (const other of others) {
    if (other.resourceId === moving.resourceId) continue;
    for (const mine of xs(moving)) {
      for (const theirs of xs(other)) {
        if (Math.abs(mine - theirs) <= thresholdCm) guides.push({ axis: 'x', atCm: theirs });
      }
    }
    for (const mine of ys(moving)) {
      for (const theirs of ys(other)) {
        if (Math.abs(mine - theirs) <= thresholdCm) guides.push({ axis: 'y', atCm: theirs });
      }
    }
  }

  // One line per position: three coincident guides look like a rendering bug.
  return dedupe(guides);
}

function dedupe(guides: readonly Guide[]): Guide[] {
  const seen = new Set<string>();
  return guides.filter((guide) => {
    const key = `${guide.axis}:${guide.atCm}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/* ------------------------------- validation ------------------------------- */

export const LABEL_MAX = 8;
export const SIZE_MIN_CM = 40;
export const SIZE_MAX_CM = 400;

/**
 * § 6.4 — a desk is 40–400 cm; a room or an office is a drawn rectangle and
 * "wildly different sizes" is the point of it, so it goes up to a floor's
 * width. Lockers and parking spaces keep their own plausible range.
 */
export const SIZE_LIMITS_CM: Record<EditorObject['kind'], { min: number; max: number }> = {
  desk: { min: SIZE_MIN_CM, max: SIZE_MAX_CM },
  locker: { min: 20, max: SIZE_MAX_CM },
  parking: { min: 100, max: 1000 },
  room: { min: 100, max: 5000 },
  office: { min: 100, max: 5000 },
};

export type LabelProblem = 'required' | 'too_long' | 'duplicate';

/** § 6.4 — the label is unique per version; the server enforces it too. */
export function validateLabel(
  label: string,
  objects: readonly EditorObject[],
  ownId: string,
): LabelProblem | null {
  const trimmed = label.trim();
  if (trimmed === '') return 'required';
  if (trimmed.length > LABEL_MAX) return 'too_long';
  const clash = objects.some((object) => object.resourceId !== ownId && object.label.trim() === trimmed);
  return clash ? 'duplicate' : null;
}

export function clampSize(valueCm: number, kind: EditorObject['kind'] = 'desk'): number {
  const limits = SIZE_LIMITS_CM[kind];
  return Math.min(limits.max, Math.max(limits.min, Math.round(valueCm)));
}

/* -------------------------------- resizing -------------------------------- */

/** The eight grips of a selected rectangle, named by the edge(s) they move. */
export type ResizeHandle = 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'nw';
export const RESIZE_HANDLES: readonly ResizeHandle[] = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'];
/** § 6.4 — the kinds drawn as rectangles of their own size; a desk is sized in its panel. */
export const RESIZABLE_KINDS: readonly EditorObject['kind'][] = ['room', 'office'];

export interface ResizeInput {
  object: EditorObject;
  handle: ResizeHandle;
  /** The pointer's travel since the grip was taken, in plan cm (world axes). */
  deltaXCm: number;
  deltaYCm: number;
  gridCm: number;
  snap: boolean;
  bounds: Bounds;
}

/**
 * Drag a grip: the edge under it follows the pointer, the opposite edge stays
 * where it is — also for a rotated rectangle, whose pivot is its centre and
 * would otherwise walk off with every change of size. The pointer's travel is
 * turned into the rectangle's own axes first; the moved edge snaps to the
 * grid; the size keeps to the kind's range and the plan's bounds.
 */
export function resizeObject(input: ResizeInput): EditorObject {
  const { object, handle, gridCm, snap, bounds } = input;
  const theta = (object.rotationDeg * Math.PI) / 180;
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  // World → local: rotate the travel back by the object's angle.
  const localDx = input.deltaXCm * cos + input.deltaYCm * sin;
  const localDy = -input.deltaXCm * sin + input.deltaYCm * cos;
  const limits = SIZE_LIMITS_CM[object.kind];

  // Local extents around the old centre; the grip's edges move, the rest stay.
  let left = -object.wCm / 2;
  let right = object.wCm / 2;
  let top = -object.hCm / 2;
  let bottom = object.hCm / 2;
  const grid = (value: number): number => snapToGrid(value, gridCm, snap);
  if (handle.includes('e'))
    right = Math.max(left + limits.min, Math.min(left + limits.max, grid(right + localDx)));
  if (handle.includes('w'))
    left = Math.min(right - limits.min, Math.max(right - limits.max, grid(left + localDx)));
  if (handle.includes('s'))
    bottom = Math.max(top + limits.min, Math.min(top + limits.max, grid(bottom + localDy)));
  if (handle.includes('n'))
    top = Math.min(bottom - limits.min, Math.max(bottom - limits.max, grid(top + localDy)));

  const wCm = Math.round(right - left);
  const hCm = Math.round(bottom - top);
  // The new centre, in local coordinates of the old one, then in the world.
  const shiftX = (left + right) / 2;
  const shiftY = (top + bottom) / 2;
  const centreX = object.xCm + object.wCm / 2 + shiftX * cos - shiftY * sin;
  const centreY = object.yCm + object.hCm / 2 + shiftX * sin + shiftY * cos;

  return clampToBounds(
    { ...object, wCm, hCm, xCm: Math.round(centreX - wCm / 2), yCm: Math.round(centreY - hCm / 2) },
    bounds,
  );
}
